import type { Division } from '@core/types/api/Division/Division';
import Enums from '@core/types/api/Enums';
import type { Market } from '@core/types/api/Market/Market';
import type { Plant } from '@core/types/api/Plant/Plant';
import type { List } from '@core/types/list';
import type { Path } from '@core/utils/types';
import { NIL_UUID } from '@core/utils/uuid';
import type { TableDataModel } from '@web/components/DataTable/TableDataModel';
import type { SelectorOption } from '@web/components/Selector/Selector';

import type { FilterByOpts, NamedFilterOpts } from './ApiClient';

export const getMarketOptsFromList = (
  marketList: List<Market>,
  divisionIDs: string[] | undefined,
  includeNoMarkets?: boolean,
): SelectorOption[] => {
  const options: SelectorOption[] = [];

  const filteredMarkets =
    divisionIDs === undefined || divisionIDs.length === 0
      ? marketList.items
      : marketList.items.filter((m) => m.divisionId !== null && divisionIDs.includes(m.divisionId));

  if (includeNoMarkets === true) {
    options.push({
      id: NIL_UUID,
      name: 'No markets',
    });
  }

  options.push(
    ...filteredMarkets.map((m) => ({
      id: m.id,
      name: m.name,
    })),
  );

  return options;
};

export const getPlantOptsFromList = (
  plantList: List<Plant>,
  marketIDs: string[] | undefined,
  divisionIDs: string[] | undefined,
  hideArchived?: boolean,
): SelectorOption[] => {
  const options: SelectorOption[] = [];

  let filteredPlants = plantList.items;

  if (hideArchived === true) {
    filteredPlants = filteredPlants.filter((p) => !p.isArchived);
  }

  // If a market is selected, filter down to plants in that market.
  // If no market is selected but a division is selected, filter down to plants in that division.
  if (marketIDs !== undefined && marketIDs.length > 0) {
    filteredPlants = filteredPlants.filter((p) => marketIDs.includes(p.market.id));
  } else if (divisionIDs !== undefined && divisionIDs.length > 0) {
    filteredPlants = filteredPlants.filter(
      (p) => p.market.divisionId !== null && divisionIDs.includes(p.market.divisionId),
    );
  }

  options.push(
    ...filteredPlants.map((p) => ({
      id: p.id,
      name: p.name,
    })),
  );

  return options;
};

export const getDivisionOptsFromList = (divisionList: List<Division>): SelectorOption[] => {
  return divisionList.items.map((d) => ({ id: d.id, name: d.name }));
};

type NilFilterConfig = {
  operation: Enums.FilterOperation;
  value: string;
};

/**
 * Returns an async setter function that adds/removes a named filter on the given tableData.
 * Pass `nilFilter` for fields that also have an EqualsOrNull companion (e.g. marketId).
 */
export const makeSetNamedFilter =
  <TRow extends { id: string }, TAgg extends { id: string }>(
    tableData: TableDataModel<TRow, TAgg>,
    config: {
      fieldName: string;
      operation?: Enums.FilterOperation;
      nilFilter?: NilFilterConfig;
    },
  ) =>
  async (values: string[]): Promise<void> => {
    const op = config.operation ?? Enums.FilterOperation.Equals;
    const filter: FilterByOpts<TRow> = {
      name: config.fieldName as Path<TRow>,
      operation: op,
      values,
    };
    if (config.nilFilter !== undefined) {
      const nf: FilterByOpts<TRow> = {
        name: config.fieldName as Path<TRow>,
        operation: config.nilFilter.operation,
        values: [config.nilFilter.value],
      };
      if (values.length === 0) {
        await tableData.removeFilters(filter, nf);
      } else {
        await tableData.replaceFilters([nf], [filter]);
      }
    } else if (values.length === 0) {
      await tableData.removeFilters(filter);
    } else {
      await tableData.addFilters(filter);
    }
  };

export const getFilterValuesFromTableModel = <
  TRow extends { id: string },
  TAgg extends { id: string },
>(
  tableModel: TableDataModel<TRow, TAgg>,
  filterNames: string[],
): { [key: string]: string[] } => {
  const filters = tableModel.getAllColumnFilters() as NamedFilterOpts<TRow>[];
  const filteredFilters = filters.filter((f) => filterNames.includes(f.name));
  const result: { [key: string]: string[] } = {};

  filteredFilters.forEach((item) => {
    result[item.name] = item.values as string[];
  });
  return result;
};
